import { getLocaleString, Language } from './formatUtils';

export type PeriodValue = 'month' | 'year' | 'total';

export interface PeriodRanges {
  start: Date;
  end: Date;
  prevStart: Date;
  prevEnd: Date;
  comparisonDate: string;
}

export function getPeriodRanges(
  period: PeriodValue,
  language: Language,
  referenceDate: Date = new Date(),
): PeriodRanges {
  const now = referenceDate;

  if (period === 'year') {
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const prevYear = now.getFullYear() - 1;
    const prevMonth = now.getMonth();
    const currentDate = now.getDate();
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();
    const adjustedPrevDate = Math.min(currentDate, daysInPrevMonth);

    const prevStart = new Date(prevYear, 0, 1);
    const prevEnd = new Date(prevYear, prevMonth, adjustedPrevDate, 23, 59, 59, 999);

    const comparisonDate = new Date(prevYear, prevMonth, adjustedPrevDate);
    const locale = getLocaleString(language);
    const formattedDate = comparisonDate.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return { start, end, prevStart, prevEnd, comparisonDate: formattedDate };
  }

  if (period === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const currentDate = now.getDate();
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();
    const adjustedPrevDate = Math.min(currentDate, daysInPrevMonth);

    const prevStart = new Date(prevYear, prevMonth, 1);
    const prevEnd = new Date(prevYear, prevMonth, adjustedPrevDate, 23, 59, 59, 999);

    const comparisonDate = new Date(prevYear, prevMonth, adjustedPrevDate);
    const locale = getLocaleString(language);
    const formattedDate = comparisonDate.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
    });

    return { start, end, prevStart, prevEnd, comparisonDate: formattedDate };
  }

  return {
    start: new Date(0),
    end: new Date(8640000000000000),
    prevStart: new Date(0),
    prevEnd: new Date(0),
    comparisonDate: '',
  };
}

function formatPeriodBoundary(date: Date, language: Language, includeYear: boolean): string {
  const locale = getLocaleString(language);
  const day = date.getDate();

  if (language === 'fr') {
    const dayStr = day === 1 ? '1er' : String(day);
    const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
    return includeYear ? `${dayStr} ${month} ${date.getFullYear()}` : `${dayStr} ${month}`;
  }

  const options: Intl.DateTimeFormatOptions = includeYear
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' };

  return new Intl.DateTimeFormat(locale, options).format(date);
}

/** Numeric short boundary, e.g. FR "1/1" or "7/8/26". */
function formatPeriodBoundaryCompact(date: Date, language: Language, includeYear: boolean): string {
  const locale = getLocaleString(language);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    ...(includeYear ? { year: '2-digit' as const } : {}),
  }).format(date);
}

export type PeriodRangeLabelVariant = 'full' | 'compact';

/** Range beside period toggle. Full: "1er janv. – 6 août 2026". Compact: "1/1 – 6/8/26". */
export function formatPeriodRangeLabel(
  period: PeriodValue,
  language: Language,
  referenceDate: Date = new Date(),
  variant: PeriodRangeLabelVariant = 'full',
): string | null {
  if (period === 'total') {
    return null;
  }

  const { start, end } = getPeriodRanges(period, language, referenceDate);
  const includeYearOnStart = start.getFullYear() !== end.getFullYear();

  if (variant === 'compact') {
    const startLabel = formatPeriodBoundaryCompact(start, language, includeYearOnStart);
    const endLabel = formatPeriodBoundaryCompact(end, language, true);
    return `${startLabel} – ${endLabel}`;
  }

  const startLabel = formatPeriodBoundary(start, language, includeYearOnStart);
  const endLabel = formatPeriodBoundary(end, language, true);
  return `${startLabel} – ${endLabel}`;
}
